import type {FileMetadata, FileStorageAdapter} from "@/types/FileStorageInterfaces.ts";
import { useAuthClient } from '@/stores/auth-store.ts'

const { post, get, del } = useAuthClient()

export class ServerAdapter implements FileStorageAdapter {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async store(file: File | Uint8Array, metadata: Omit<FileMetadata, 'id'>, id?: string): Promise<string> {
    const formData = new FormData();

    if (file instanceof File) {
      formData.append('file', file);
    } else {
      const blob = new Blob([file], { type: metadata.mimeType });
      formData.append('file', blob, metadata.filename);
    }

    formData.append('eventId', metadata.eventId.toString());
    formData.append('fileType', metadata.fileType);
    formData.append('mimeType', metadata.mimeType);

    // Include the ID if provided
    if (id) {
      formData.append('id', id);
    }

    try {
      const response = await post(`${this.baseUrl}/upload-file`, formData)
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      return id || result.storageId || result.filename;
    } catch (error) {
      console.error('Error uploading to server:', error);
      throw error;
    }
  }

  async retrieve(id: string): Promise<Blob> {
    try {
      const response = await get(`${this.baseUrl}/files/id/${id}`)
      if (!response.ok) {
        throw new Error(`Failed to retrieve file: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error retrieving file from server:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<boolean> {
    try {
      const response = await del(`${this.baseUrl}/files/id/${id}`)
      return response.ok || response.status === 404;
    } catch (error) {
      console.error('Error deleting file from server:', error);
      return false;
    }
  }

  async deleteWithStatus(id: string): Promise<{ ok: boolean; status: number }> {
    try {
      const response = await del(`${this.baseUrl}/files/id/${id}`)
      return { ok: response.ok, status: response.status };
    } catch (error) {
      console.error('Error deleting file from server:', error);
      return { ok: false, status: 0 };
    }
  }

  async getUrl(id: string): Promise<string> {
    const blob = await this.retrieve(id);
    return URL.createObjectURL(blob);
    // return `${this.baseUrl}/files/${id}.jpg?token=${this.authToken}`;
  }

  async exists(id: string): Promise<boolean> {
    try {
      const response = await get(`${this.baseUrl}/files/id/${id}/exists`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  async reconcile(id: string): Promise<{
    exists: boolean;
    size?: number;
    checksumSha256?: string;
    etag?: string;
    contentType?: string;
    lastModified?: string;
  }> {
    try {
      const response = await get(`${this.baseUrl}/files/id/${id}/reconcile`);
      if (response.status === 404) {
        return { exists: false };
      }
      if (!response.ok) {
        throw new Error(`Failed to reconcile file: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error reconciling file on server:', error);
      throw error;
    }
  }
}
